/**
 * Workflow configuration for DemandeAbsence — validation steps, type demande
 * config and permission requirements.
 *
 * Status states/transitions/colors were collapsed out of this file (Shape C,
 * plan-business-semantic §2.5.2) into ./statuses.ts, which now drives the
 * shared StatusBadge via L2.5 tokens.
 */

interface ValidationStep {
  code: string;
  label: string;
  order: number;
  description: string;
}

interface TypeDemandeConfigEntry {
  code: string;
  label: string;
  requiresSolde: boolean;
  canBeUrgent: boolean;
  isHeureType?: boolean;
  requiresDocument?: boolean;
}

interface PermissionRequirements {
  create: string[];
  createForOthers: string[];
  validate: string[];
  cancel: string[];
  cancelAny: string[];
  view: string[];
}

// Validation steps configuration
export const VALIDATION_STEPS: ValidationStep[] = [
  {
    code: "validation_donnees",
    label: "Validation des données",
    order: 1,
    description: "Vérification des dates, conflits et solde",
  },
  {
    code: "validation_rh",
    label: "Validation RH",
    order: 2,
    description: "Validation par un membre du personnel RH",
  },
];

// Type demande configuration
export const TYPE_DEMANDE_CONFIG: Record<string, TypeDemandeConfigEntry> = {
  CONGE_PAYE: {
    code: "CONGE_PAYE",
    label: "Congé payé",
    requiresSolde: true,
    canBeUrgent: true,
  },
  RTT: {
    code: "RTT",
    label: "RTT",
    requiresSolde: true,
    canBeUrgent: true,
  },
  ARRET_MALADIE: {
    code: "ARRET_MALADIE",
    label: "Arrêt maladie",
    requiresDocument: true,
    canBeUrgent: true,
    requiresSolde: false,
  },
  HEURE_SUP: {
    code: "HEURE_SUP",
    label: "Heures supplémentaires",
    requiresSolde: false,
    canBeUrgent: false,
    isHeureType: true,
  },
  RECUP_HEURE_SUP: {
    code: "RECUP_HEURE_SUP",
    label: "Récupération heures sup",
    requiresSolde: true,
    canBeUrgent: false,
    isHeureType: true,
  },
  PAIEMENT_HEURE_SUP: {
    code: "PAIEMENT_HEURE_SUP",
    label: "Paiement heures sup",
    requiresSolde: true,
    canBeUrgent: false,
    isHeureType: true,
  },
};

/**
 * True for types measured in hours (heures sup / récup / paiement) rather
 * than working days.
 */
export function isHeureTypeCode(code: string | null | undefined): boolean {
  if (!code) return false;
  return TYPE_DEMANDE_CONFIG[code]?.isHeureType ?? false;
}

/** Code du type de demande « Paiement heures sup » (référentiel type_demande). */
export const TYPE_PAIEMENT_HEURE_SUP = "PAIEMENT_HEURE_SUP";

/**
 * True for types hidden from the employee wizard (AbsenceRequestForm in
 * EMPLOYEE mode). Paying overtime hours goes through the "Paiement d'heures
 * sup" quick action on the home dashboard, which bounds the requested amount
 * to the employee balance — something the multi-step form cannot express.
 */
export function isTypeHiddenForEmployee(code: string | null | undefined): boolean {
  return code === TYPE_PAIEMENT_HEURE_SUP;
}

/**
 * True for types that require an attached document (arrêt maladie).
 */
export function requiresDocumentCode(code: string | null | undefined): boolean {
  if (!code) return false;
  return TYPE_DEMANDE_CONFIG[code]?.requiresDocument ?? false;
}

// Permission requirements for actions
export const PERMISSION_REQUIREMENTS: PermissionRequirements = {
  create: ["rh.conges.create", "rh.conges.create:scope:self"],
  createForOthers: ["rh.conges.create_for_others"],
  validate: ["rh.conges.validate"],
  cancel: ["rh.conges.cancel", "rh.conges.cancel_any"],
  cancelAny: ["rh.conges.cancel_any"],
  view: ["rh.conges.view", "rh.conges.view:scope:self"],
};
